import {
  Button,
  FloatingActionBar,
  IconButton,
  Tooltip,
} from '@dev-spendesk/grapes';
import * as NEA from '@spendesk/general-type-helpers/NonEmptyArray';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDebounce } from 'react-use';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useAccountPayableSuppliersListQuery } from 'modules/bookkeep/accounts-payable/hooks/useAccountPayableSuppliersListQuery';
import { useGetSuppliersWithoutAccountPayableListQuery } from 'modules/bookkeep/accounts-payable/hooks/useGetSuppliersWithoutAccountPayableListQuery';
import { RulesPromoBanner } from 'modules/bookkeep/settings/integrations/pages/PayableRulesSetupPage/components/RulesPromoBanner';
import { useParams } from 'src/core/common/hooks/useParams';
import { routeFor, routes } from 'src/core/constants/routes';
import { apiUrl } from 'src/core/utils/api';

import { AccountPayableSuppliersListSearch } from './AccountPayableSuppliersListSearch';
import { AccountPayableSuppliersMainList } from './AccountPayableSuppliersMainList';
import { type IntegrationStatus } from '../../../integration/status';
import { useBulkArchiveSupplierMutation } from '../../hooks/useBulkArchiveSupplierMutation';
import { useBulkUnarchiveSupplierMutation } from '../../hooks/useBulkUnarchiveSupplierMutation';

type Props = {
  integrationStatus: IntegrationStatus | undefined;
  isFileViewerOpen: boolean;
  setIsSupplierCreationModalOpen: (isModalOpen: boolean) => void;
};

export const AccountPayableSuppliersList = ({
  integrationStatus,
  isFileViewerOpen,
  setIsSupplierCreationModalOpen,
}: Props) => {
  const history = useHistory();
  const { list } = useParams(routes.COMPANY_ACCOUNTS_PAYABLE.path);

  const { t } = useTranslation('global');
  const companyId = useCompanyId();
  const selectedTab = (list ?? 'current') as 'current' | 'archived';
  const { search } = useLocation();
  const [supplierSelectionToBulkArchive, setSupplierSelectionToBulkArchive] =
    useState<string[]>([]);
  const [accountPayableIdToBulkArchive, setAccountPayableIdToBulkArchive] =
    useState<string | undefined>();
  const [bulkArchiveSupplier] = useBulkArchiveSupplierMutation();
  const [bulkUnarchiveSupplier] = useBulkUnarchiveSupplierMutation();

  const parsedSearch =
    (queryString.parse(search).search as string | undefined) || '';
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

  useDebounce(
    () => {
      setDebouncedSearchValue(parsedSearch);
    },
    500,
    [parsedSearch],
  );

  useEffect(() => {
    setSupplierSelectionToBulkArchive([]);
    setAccountPayableIdToBulkArchive(undefined);
  }, [selectedTab]);

  const integrationName = integrationStatus?.integration;
  const mappedOnly =
    integrationName !== 'Datev' &&
    integrationName !== 'SpendeskAccountingSingleEntry' &&
    integrationName !== 'SpendeskAccounting';

  const suppliersQuery = useAccountPayableSuppliersListQuery(
    debouncedSearchValue,
    selectedTab,
    mappedOnly,
  );

  const suppliersWithoutAccountPayableQuery =
    useGetSuppliersWithoutAccountPayableListQuery(
      debouncedSearchValue,
      selectedTab,
    );

  const handleBulkArchiveOrUnarchiveSelectedSuppliers = () => {
    if (accountPayableIdToBulkArchive !== undefined) {
      if (selectedTab === 'archived') {
        bulkUnarchiveSupplier({
          accountPayableIds: NEA.fromSingleItem(accountPayableIdToBulkArchive),
        });
      } else {
        bulkArchiveSupplier({
          accountPayableIds: NEA.fromSingleItem(accountPayableIdToBulkArchive),
        });
      }

      history.push(
        routeFor(routes.COMPANY_ACCOUNTS_PAYABLE.path, {
          company: companyId,
          list: selectedTab,
        }),
      );

      setSupplierSelectionToBulkArchive([]);
      setAccountPayableIdToBulkArchive(undefined);

      return;
    }

    const supplierIds = NEA.fromArray(supplierSelectionToBulkArchive);

    if (supplierIds !== undefined) {
      if (selectedTab === 'archived') {
        bulkUnarchiveSupplier({ supplierIds });
      } else {
        bulkArchiveSupplier({ supplierIds });
      }
    }

    setSupplierSelectionToBulkArchive([]);
    setAccountPayableIdToBulkArchive(undefined);
  };

  const isLoading =
    suppliersQuery.status === 'loading' ||
    suppliersWithoutAccountPayableQuery.status === 'loading' ||
    integrationStatus === undefined;

  return (
    <>
      <div className="mb-24 flex items-center">
        <AccountPayableSuppliersListSearch selectedTab={selectedTab} />
        <div className="flex-1" />
        <a
          rel="noopener noreferrer"
          target="_blank"
          href={apiUrl(
            `/accounting-integration/chart-of-accounts/supplier-accounts/export`,
            companyId,
          )}
          aria-label={t('bookkeep.accountsPayable.list.downloadSupplierList')}
        >
          <Tooltip
            content={t('bookkeep.accountsPayable.list.downloadSupplierList')}
            triggerAsChild
          >
            <IconButton
              iconName="arrow-down-tray"
              variant="secondaryNeutral"
              className="ml-8"
              aria-label=""
              isDisabled={isLoading}
            />
          </Tooltip>
        </a>
        <Button
          variant="primaryBrand"
          className="ml-8"
          text={t('bookkeep.accountsPayable.list.createSupplier')}
          onClick={() => setIsSupplierCreationModalOpen(true)}
          isDisabled={isLoading}
        />
      </div>
      <RulesPromoBanner className="my-24" />
      <AccountPayableSuppliersMainList
        integrationStatus={integrationStatus}
        suppliers={
          suppliersQuery.status === 'success' ? suppliersQuery.data : []
        }
        suppliersWithoutAccountPayable={
          suppliersWithoutAccountPayableQuery.status === 'success'
            ? suppliersWithoutAccountPayableQuery.data
            : { logosUrls: [], suppliers: [], totalCount: 0 }
        }
        selectedTab={selectedTab}
        hasNextPage={suppliersQuery.hasNextPage}
        onFetchNextPage={suppliersQuery.fetchNextPage}
        supplierSelectionToBulkArchive={supplierSelectionToBulkArchive}
        isFileViewerOpen={isFileViewerOpen}
        setSupplierSelectionToBulkArchive={(_, id, checked) => {
          setSupplierSelectionToBulkArchive((options) => {
            if (checked) {
              return options.concat(id);
            }
            return options.filter((optionId) => optionId !== id);
          });
        }}
        onAllSuppliersToBulkArchiveSelectionChange={(_, ids, checked) => {
          setSupplierSelectionToBulkArchive(checked ? ids : []);
        }}
        setAccountPayableIdToBulkArchive={setAccountPayableIdToBulkArchive}
        isLoading={isLoading}
      />

      {supplierSelectionToBulkArchive.length > 0 && (
        <FloatingActionBar
          actions={[
            {
              text:
                selectedTab === 'archived'
                  ? t('bookkeep.accountsPayable.panel.bulkUnarchive')
                  : t('bookkeep.accountsPayable.panel.bulkArchive'),
              iconName:
                selectedTab === 'archived' ? 'arrow-turn-right' : 'trash',
              onClick: () => handleBulkArchiveOrUnarchiveSelectedSuppliers(),
            },
          ]}
        >
          {t('misc.selection', {
            count: supplierSelectionToBulkArchive.length,
          })}
        </FloatingActionBar>
      )}
    </>
  );
};
